import React, { useState, useEffect } from "react";
import { sbFetch } from "../utils/supabaseClient";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from "recharts";
import { SVG_ICONS } from "./TablerIconPicker";

const OriMetricsReport = ({ timeRange }) => {
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [sentimentChartData, setSentimentChartData] = useState([]);
  const [stats, setStats] = useState({ thumbsUp: 0, thumbsDown: 0, positive: 0, negative: 0, neutral: 0 });
  const [selectedRating, setSelectedRating] = useState(null);
  const [selectedSentiment, setSelectedSentiment] = useState(null);
  const [expandedComments, setExpandedComments] = useState({});

  useEffect(() => {
    loadFeedback();
  }, [timeRange]);

  const analyzeSentiment = (rating, feedbackText) => {
    // If no comment provided, sentiment is neutral regardless of rating
    if (!feedbackText || feedbackText.trim() === "") return "neutral";
    // Otherwise, sentiment is based on the user's explicit rating
    if (rating === "thumbs_up") return "positive";
    if (rating === "thumbs_down") return "negative";
    return "neutral";
  };

  const loadFeedback = async () => {
    try {
      setLoading(true);

      // Fetch all feedback
      const feedbackData = await sbFetch("GET", "ori_feedback").catch(() => []);

      if (!Array.isArray(feedbackData)) {
        setFeedback([]);
        setChartData([]);
        setStats({ thumbsUp: 0, thumbsDown: 0 });
        setLoading(false);
        return;
      }

      // Filter by time range
      const now = new Date();
      const startDate = new Date(now);
      startDate.setDate(startDate.getDate() - timeRange);

      const filteredFeedback = feedbackData.filter(item => {
        const itemDate = new Date(item.created_at || item.createdAt || item.addedAt || 0);
        return itemDate >= startDate && itemDate <= now;
      });

      // Count thumbs up and down based on rating field
      const thumbsUp = filteredFeedback.filter(f => f.rating === "thumbs_up").length;
      const thumbsDown = filteredFeedback.filter(f => f.rating === "thumbs_down").length;

      // Add sentiment based on user's explicit rating (neutral if no comment)
      const feedbackWithSentiment = filteredFeedback.map(f => ({
        ...f,
        sentiment: analyzeSentiment(f.rating, f.feedback_text),
        isPositive: f.rating === "thumbs_up"
      })).sort((a, b) => new Date(b.created_at || b.createdAt || b.addedAt) - new Date(a.created_at || a.createdAt || a.addedAt));

      // Count sentiment distribution
      const positive = feedbackWithSentiment.filter(f => f.sentiment === "positive").length;
      const negative = feedbackWithSentiment.filter(f => f.sentiment === "negative").length;
      const neutral = feedbackWithSentiment.filter(f => f.sentiment === "neutral").length;

      // Prepare rating chart data (green/amber/red)
      const ratingChart = [
        { name: "Thumbs Up", value: thumbsUp, fill: "#10B981" },
        { name: "Thumbs Down", value: thumbsDown, fill: "#EF4444" }
      ];

      // Prepare sentiment chart data (green/amber/red)
      const sentimentChart = [];
      if (positive > 0) sentimentChart.push({ name: "Positive", value: positive, fill: "#10B981" });
      if (neutral > 0) sentimentChart.push({ name: "Neutral", value: neutral, fill: "#F59E0B" });
      if (negative > 0) sentimentChart.push({ name: "Negative", value: negative, fill: "#EF4444" });

      setFeedback(feedbackWithSentiment);
      setChartData(ratingChart);
      setSentimentChartData(sentimentChart);
      setStats({ thumbsUp, thumbsDown, positive, negative, neutral });
    } catch (err) {
      console.error("Error loading ORI feedback:", err);
      setFeedback([]);
      setChartData([]);
      setSentimentChartData([]);
      setStats({ thumbsUp: 0, thumbsDown: 0, positive: 0, negative: 0, neutral: 0 });
    } finally {
      setLoading(false);
    }
  };

  const getSentimentIcon = (sentiment) => {
    switch(sentiment) {
      case "positive": return SVG_ICONS["smile"]({ size: 28, color: "#10B981" });
      case "negative": return SVG_ICONS["frown"]({ size: 28, color: "#EF4444" });
      case "neutral": return SVG_ICONS["meh"]({ size: 28, color: "#F59E0B" });
      default: return SVG_ICONS["meh"]({ size: 28, color: "#F59E0B" });
    }
  };

  const getRatingIcon = (isPositive) => {
    if (isPositive) {
      return (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M6 9c0-1 1-2 2-2h1V3c0-1 1-2 2-2s2 1 2 2v4h4c1 0 2 1 2 2v6c0 1-1 2-2 2h-8c-1 0-2-1-2-2V9z"/>
          <line x1="6" y1="9" x2="6" y2="15"/>
        </svg>
      );
    } else {
      return (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 15c0 1-1 2-2 2h-1v4c0 1-1 2-2 2s-2-1-2-2v-4h-4c-1 0-2-1-2-2V9c0-1 1-2 2-2h8c1 0 2 1 2 2v6z"/>
          <line x1="18" y1="15" x2="18" y2="9"/>
        </svg>
      );
    }
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const total = stats.thumbsUp + stats.thumbsDown;
      const percentage = total > 0 ? Math.round((data.value / total) * 100) : 0;
      return (
        <div style={{
          background: "#050e1a",
          border: "1px solid rgba(59, 130, 246, 0.2)",
          borderRadius: "6px",
          padding: "8px 12px",
          color: "#fff",
          fontSize: "12px"
        }}>
          <div>{data.name}</div>
          <div style={{ color: "#5B9EF0" }}>{data.value} feedback ({percentage}%)</div>
        </div>
      );
    }
    return null;
  };

  const CustomSentimentTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const total = stats.positive + stats.negative + stats.neutral;
      const percentage = total > 0 ? Math.round((data.value / total) * 100) : 0;
      return (
        <div style={{
          background: "#050e1a",
          border: "1px solid rgba(59, 130, 246, 0.2)",
          borderRadius: "6px",
          padding: "8px 12px",
          color: "#fff",
          fontSize: "12px"
        }}>
          <div>{data.name}</div>
          <div style={{ color: "#5B9EF0" }}>{data.value} feedback ({percentage}%)</div>
        </div>
      );
    }
    return null;
  };

  const getFilteredFeedback = () => {
    let filtered = feedback;

    if (selectedRating) {
      filtered = filtered.filter(f => {
        if (selectedRating === "thumbs_up") return f.rating === "thumbs_up";
        if (selectedRating === "thumbs_down") return f.rating === "thumbs_down";
        return true;
      });
    }

    if (selectedSentiment) {
      filtered = filtered.filter(f => f.sentiment === selectedSentiment);
    }

    return filtered;
  };

  const handleRatingClick = (name) => {
    if (selectedRating === name) {
      setSelectedRating(null);
    } else {
      setSelectedRating(name === "Thumbs Up" ? "thumbs_up" : "thumbs_down");
      setSelectedSentiment(null);
    }
  };

  const handleSentimentClick = (name) => {
    if (selectedSentiment === name.toLowerCase()) {
      setSelectedSentiment(null);
    } else {
      setSelectedSentiment(name.toLowerCase());
      setSelectedRating(null);
    }
  };

  if (loading) return <div className="loading">Loading ORI feedback...</div>;

  const totalFeedback = stats.thumbsUp + stats.thumbsDown;
  const positivePercentage = totalFeedback > 0 ? Math.round((stats.thumbsUp / totalFeedback) * 100) : 0;
  const filteredFeedback = getFilteredFeedback();

  return (
    <div style={{ padding: "20px" }}>
      <div style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "20px",
        marginBottom: "30px"
      }}>
        {/* Stats Cards */}
        <div style={{
          padding: "20px",
          background: "rgba(59, 130, 246, 0.08)",
          border: "1px solid rgba(59, 130, 246, 0.2)",
          borderRadius: "8px",
          textAlign: "center"
        }}>
          <div style={{ color: "#7A9CC4", fontSize: "12px", textTransform: "uppercase", marginBottom: "8px" }}>Total Feedback</div>
          <div style={{ color: "#5B9EF0", fontSize: "32px", fontWeight: "700" }}>{totalFeedback}</div>
        </div>

        <div style={{
          padding: "20px",
          background: "rgba(16, 185, 129, 0.08)",
          border: "1px solid rgba(16, 185, 129, 0.2)",
          borderRadius: "8px",
          textAlign: "center"
        }}>
          <div style={{ color: "#7A9CC4", fontSize: "12px", textTransform: "uppercase", marginBottom: "8px" }}>Positive Rate</div>
          <div style={{ color: "#10B981", fontSize: "32px", fontWeight: "700" }}>{positivePercentage}%</div>
        </div>
      </div>

      {/* Charts Grid */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "20px",
        marginBottom: "30px"
      }}>
        {/* Rating Distribution Chart */}
        {chartData.length > 0 && (stats.thumbsUp > 0 || stats.thumbsDown > 0) ? (
          <div style={{
            background: "rgba(59, 130, 246, 0.05)",
            border: "1px solid rgba(59, 130, 246, 0.15)",
            borderRadius: "8px",
            padding: "20px"
          }}>
            <h3 style={{ color: "#5B9EF0", margin: "0 0 20px 0" }}>Rating Distribution</h3>
            <div style={{ width: "100%", height: "300px" }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={90}
                    paddingAngle={5}
                    dataKey="value"
                    onClick={(entry) => handleRatingClick(entry.name)}
                    style={{ cursor: "pointer" }}
                  >
                    {chartData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={selectedRating === (entry.name === "Thumbs Up" ? "thumbs_up" : "thumbs_down") ? entry.fill : `${entry.fill}99`}
                        style={{ opacity: selectedRating === (entry.name === "Thumbs Up" ? "thumbs_up" : "thumbs_down") ? 1 : 0.6 }}
                      />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ paddingTop: "20px" }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            {selectedRating && (
              <div style={{ marginTop: "12px", textAlign: "center", color: "#7A9CC4", fontSize: "12px" }}>
                Click to clear filter
              </div>
            )}
          </div>
        ) : (
          <div style={{
            background: "rgba(59, 130, 246, 0.05)",
            border: "1px solid rgba(59, 130, 246, 0.15)",
            borderRadius: "8px",
            padding: "20px",
            textAlign: "center",
            color: "#7A9CC4"
          }}>
            <h3 style={{ color: "#5B9EF0", margin: "0 0 20px 0" }}>Rating Distribution</h3>
            <p>No feedback data available</p>
          </div>
        )}

        {/* Sentiment Distribution Chart */}
        {sentimentChartData.length > 0 ? (
          <div style={{
            background: "rgba(59, 130, 246, 0.05)",
            border: "1px solid rgba(59, 130, 246, 0.15)",
            borderRadius: "8px",
            padding: "20px"
          }}>
            <h3 style={{ color: "#5B9EF0", margin: "0 0 20px 0" }}>Sentiment Distribution</h3>
            <div style={{ width: "100%", height: "300px" }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={sentimentChartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={90}
                    paddingAngle={5}
                    dataKey="value"
                    onClick={(entry) => handleSentimentClick(entry.name)}
                    style={{ cursor: "pointer" }}
                  >
                    {sentimentChartData.map((entry, index) => (
                      <Cell
                        key={`sentiment-${index}`}
                        fill={selectedSentiment === entry.name.toLowerCase() ? entry.fill : `${entry.fill}99`}
                        style={{ opacity: selectedSentiment === entry.name.toLowerCase() ? 1 : 0.6 }}
                      />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomSentimentTooltip />} />
                  <Legend wrapperStyle={{ paddingTop: "20px" }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            {selectedSentiment && (
              <div style={{ marginTop: "12px", textAlign: "center", color: "#7A9CC4", fontSize: "12px" }}>
                Click to clear filter
              </div>
            )}
          </div>
        ) : (
          <div style={{
            background: "rgba(59, 130, 246, 0.05)",
            border: "1px solid rgba(59, 130, 246, 0.15)",
            borderRadius: "8px",
            padding: "20px",
            textAlign: "center",
            color: "#7A9CC4"
          }}>
            <h3 style={{ color: "#5B9EF0", margin: "0 0 20px 0" }}>Sentiment Distribution</h3>
            <p>No feedback data available</p>
          </div>
        )}
      </div>

      {/* Feedback Comments */}
      <div style={{
        background: "rgba(59, 130, 246, 0.05)",
        border: "1px solid rgba(59, 130, 246, 0.15)",
        borderRadius: "8px",
        padding: "20px"
      }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "20px" }}>
          <h3 style={{ color: "#5B9EF0", margin: "0" }}>Feedback Comments ({filteredFeedback.length})</h3>
          {(selectedRating || selectedSentiment) && (
            <button
              onClick={() => {
                setSelectedRating(null);
                setSelectedSentiment(null);
              }}
              style={{
                padding: "6px 12px",
                background: "#5B9EF0",
                border: "none",
                borderRadius: "4px",
                color: "#fff",
                fontSize: "12px",
                cursor: "pointer",
                fontWeight: "600"
              }}
            >
              Clear Filter
            </button>
          )}
        </div>

        {filteredFeedback.length === 0 ? (
          <div style={{ color: "#7A9CC4", textAlign: "center", padding: "20px" }}>
            {selectedRating || selectedSentiment ? "No feedback matches selected filter" : "No feedback available"}
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {filteredFeedback.map((item, idx) => {
              const isExpanded = expandedComments[idx];
              const comment = item.feedback_text || "(No comment)";
              const isLong = comment.length > 80;
              const displayText = isExpanded ? comment : comment.substring(0, 80);

              return (
                <div
                  key={idx}
                  style={{
                    padding: "12px 16px",
                    background: "rgba(59, 130, 246, 0.05)",
                    border: "1px solid rgba(59, 130, 246, 0.1)",
                    borderRadius: "6px",
                    borderLeft: `4px solid ${item.isPositive ? "#10B981" : "#EF4444"}`,
                    display: "grid",
                    gridTemplateColumns: "auto 1fr",
                    gap: "16px",
                    alignItems: "start"
                  }}
                >
                  {/* Left Column: Icons */}
                  <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                    <div>{getSentimentIcon(item.sentiment)}</div>
                    <div>{getRatingIcon(item.isPositive)}</div>
                  </div>

                  {/* Right Column: Date (top) & Comment (bottom) */}
                  <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                    {/* Date and Email at top */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px" }}>
                      <div style={{ color: "#7A9CC4", fontSize: "11px" }}>
                        {new Date(item.created_at || item.createdAt || item.addedAt).toLocaleDateString()} at {new Date(item.created_at || item.createdAt || item.addedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </div>
                      {item.email && (
                        <a
                          href={`/users?email=${encodeURIComponent(item.email)}`}
                          style={{
                            color: "#5B9EF0",
                            fontSize: "11px",
                            textDecoration: "none",
                            cursor: "pointer",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxWidth: "200px"
                          }}
                          title={item.email}
                        >
                          {item.email}
                        </a>
                      )}
                    </div>

                    {/* Comment at bottom with more/less */}
                    <p style={{
                      color: "#9DB4D4",
                      fontSize: "13px",
                      margin: "0",
                      lineHeight: "1.5",
                      wordBreak: "break-word"
                    }}>
                      {displayText}
                      {isLong && (
                        <span
                          onClick={() => setExpandedComments(prev => ({ ...prev, [idx]: !isExpanded }))}
                          style={{
                            color: "#5B9EF0",
                            cursor: "pointer",
                            fontWeight: "600",
                            marginLeft: "4px"
                          }}
                        >
                          {isExpanded ? "...less" : "...more"}
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default OriMetricsReport;
